/**
 * DashboardHomeHeader — Cluster H.5 (Feb 2026).
 *
 * Replaces the "بيّنة" / "Bayenah" wordmark on the empty-state home
 * with the official Bayenah logo PNG (language-agnostic), and adds:
 *   • a time-aware greeting (morning / afternoon / evening)
 *   • an activity-stats ribbon (drafts, awaiting review, sent)
 *
 * Stats source: GET /api/v2/agent/dashboard/stats.
 * The previous "Welcome to Bayenah, [Name]." modal is removed at the
 * WelcomeHero call site — its functional role (orientation hints +
 * three example prompts) is replaced by this header plus the
 * RecentMattersStrip below the composer.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeBucket(now = new Date()) {
  const h = now.getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

const GREETINGS = {
  morning:   { en: 'Good morning',   ar: 'صباح الخير' },
  afternoon: { en: 'Good afternoon', ar: 'مساء الخير' },
  evening:   { en: 'Good evening',   ar: 'مساء الخير' },
};

export const DashboardHomeHeader = ({ isArabic, displayName }) => {
  const { session } = useAuth();
  const token = session?.access_token;
  const [stats, setStats] = useState({
    drafts_in_progress: null,
    awaiting_review: null,
    sent_to_colleagues: null,
  });

  useEffect(() => {
    if (!token) { setStats({ drafts_in_progress: 0, awaiting_review: 0, sent_to_colleagues: 0 }); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/v2/agent/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 5000,
        });
        if (!cancelled) setStats(res.data || {});
      } catch (_e) {
        if (!cancelled) setStats({ drafts_in_progress: 0, awaiting_review: 0, sent_to_colleagues: 0 });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const bucket = timeBucket();
  const greetingBase = GREETINGS[bucket][isArabic ? 'ar' : 'en'];
  const greeting = displayName
    ? (isArabic ? `${greetingBase}، ${displayName}` : `${greetingBase}, ${displayName}`)
    : greetingBase;

  const statLabels = isArabic
    ? {
        d: 'مسودات قيد العمل',
        r: 'بانتظار مراجعتك',
        s: 'مرسلة للزملاء',
      }
    : {
        d: 'drafts in progress',
        r: 'awaiting your review',
        s: 'sent to colleagues',
      };

  const isLoaded = stats.drafts_in_progress !== null;

  // Pre-deploy patch: zero-suppress stats. Hide individual counters
  // that are 0, and hide the ribbon entirely when ALL three are 0.
  const visibleStats = [
    { id: 'drafts', label: statLabels.d, value: stats.drafts_in_progress, testId: 'dashboard-stat-drafts' },
    { id: 'review', label: statLabels.r, value: stats.awaiting_review,    testId: 'dashboard-stat-review' },
    { id: 'sent',   label: statLabels.s, value: stats.sent_to_colleagues, testId: 'dashboard-stat-sent' },
  ].filter((s) => Number(s.value || 0) > 0);

  return (
    <div
      data-testid="dashboard-home-header"
      style={{ textAlign: 'center', marginBottom: '28px' }}
      dir={isArabic ? 'rtl' : 'ltr'}
    >
      <img
        src="/bayenah-logo-black.png"
        alt="Bayenah"
        data-testid="dashboard-home-logo"
        style={{ height: 72, width: 'auto', display: 'inline-block', objectFit: 'contain' }}
      />
      <div
        data-testid="dashboard-home-greeting"
        style={{
          fontFamily: isArabic ? 'Cairo, serif' : 'Cormorant Garamond, Georgia, serif',
          fontSize: 22,
          fontWeight: 400,
          color: '#111111',
          margin: '14px 0 6px',
          letterSpacing: '-0.2px',
        }}
      >
        {greeting}
      </div>
      {isLoaded && visibleStats.length > 0 && (
        <div
          data-testid="dashboard-home-stats"
          style={{
            // WCAG AA: #555 on white = 7.46:1 (AAA). Was #888 = 3.54:1 (FAIL AA for normal text).
            fontSize: 12,
            color: '#555555',
            letterSpacing: '0.02em',
          }}
        >
          {visibleStats.map((s, idx) => (
            <React.Fragment key={s.id}>
              {idx > 0 && (
                <span style={{ margin: '0 8px', color: '#888' }} aria-hidden="true">·</span>
              )}
              <span data-testid={s.testId}>
                {s.value} {s.label}
              </span>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardHomeHeader;
